import { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import BrandImage from "./BrandImage";
import { translateInterfaceText, type InterfaceLanguage } from "./i18n";

export type CommunityDemandSummaryItem = {
  label: string;
  memberCount: number;
  image?: string;
  meta?: string;
};

type CommunityDemandSummaryDialogProps = {
  kind: "products" | "brands" | "top-products" | "new-brands";
  items: CommunityDemandSummaryItem[];
  totalSelections: number;
  language: InterfaceLanguage;
  onClose: () => void;
};

const copy = {
  products: {
    title: "Regular products summary",
    totalLabel: "product selections",
    uniqueLabel: "unique products",
    empty: "No regular product selections yet.",
  },
  brands: {
    title: "Brands summary",
    totalLabel: "brand selections",
    uniqueLabel: "unique brands",
    empty: "No brand selections yet.",
  },
  "top-products": {
    title: "High-demand products",
    totalLabel: "active members",
    uniqueLabel: "high-demand products",
    empty: "No products are currently selected by two or more active members.",
  },
  "new-brands": {
    title: "New brands added",
    totalLabel: "approved additions",
    uniqueLabel: "new brands",
    empty: "No suggested brands have been approved yet.",
  },
} as const;

export default function CommunityDemandSummaryDialog({ kind, items, totalSelections, language, onClose }: CommunityDemandSummaryDialogProps) {
  const titleId = useId();
  const closeButton = useRef<HTMLButtonElement>(null);
  const labels = copy[kind];
  const t = (value: string) => translateInterfaceText(value, language);
  const numberLocale = language === "zh" ? "zh-CN" : language === "en" ? "en-MY" : "ms-MY";
  const isBrandSummary = kind === "brands" || kind === "new-brands";
  const rankedItems = kind === "new-brands"
    ? items
    : [...items].sort((left, right) => right.memberCount - left.memberCount || left.label.localeCompare(right.label));
  const metrics = kind === "products"
    ? [
        { value: totalSelections, label: labels.totalLabel },
        { value: rankedItems.length, label: labels.uniqueLabel },
      ]
    : kind === "brands"
      ? [{ value: totalSelections, label: labels.totalLabel }]
      : [{ value: rankedItems.length, label: labels.uniqueLabel }];

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButton.current?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [onClose]);

  return createPortal(
    <div className="community-demand-dialog" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="community-demand-dialog__window" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <header className="community-demand-dialog__header">
          <div>
            <span>{t("Latest community demand")}</span>
            <h2 id={titleId}>{t(labels.title)}</h2>
          </div>
          <button ref={closeButton} type="button" onClick={onClose} aria-label={t("Close summary")}><X /></button>
        </header>

        <div className="community-demand-dialog__metrics">
          {metrics.map((metric) => (
            <div key={metric.label}>
              <strong>{metric.value.toLocaleString(numberLocale)}</strong>
              <span>{t(metric.label)}</span>
            </div>
          ))}
        </div>

        {rankedItems.length ? (
          <ol className="community-demand-dialog__list">
            {rankedItems.map((item, index) => (
              <li key={item.label}>
                <span className="community-demand-dialog__rank" aria-label={`${t("Rank")} ${index + 1}`}>{index + 1}</span>
                {item.image
                  ? isBrandSummary
                    ? <BrandImage src={item.image} alt={item.label} wrapperClassName="community-demand-dialog__brand-image" loading="lazy" decoding="async" />
                    : <img src={item.image} alt="" loading="lazy" decoding="async" />
                  : <span className="community-demand-dialog__fallback" aria-hidden="true">{item.label.charAt(0).toUpperCase()}</span>}
                <span className="community-demand-dialog__item-copy">
                  <strong>{isBrandSummary ? item.label : t(item.label)}</strong>
                  {item.meta && <small>{isBrandSummary ? item.meta : t(item.meta)}</small>}
                </span>
              </li>
            ))}
          </ol>
        ) : <p className="community-demand-dialog__empty">{t(labels.empty)}</p>}

        {kind !== "new-brands" && <p className="community-demand-dialog__note">{t("Based only on current selections from active member accounts. Archived and deleted users are excluded.")}</p>}
      </section>
    </div>,
    document.body,
  );
}
